import { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../../components/common/Button/button';
import InputMultyLang from '../../components/common/Input/inputMultyLang';
import InputNumber from '../../components/common/Input/inputNumber';
import InputSelect from '../../components/common/Input/inputSelect';
import MultipleSelect from '../../components/common/MultipleSelect/MultipleSelect';
import ShowImage from '../../components/common/ShowImage/showImage';
import Loading from '../../components/loading';
import ToastNotification from '../../components/toastNotfication';
import { useAdvertisements } from '../../features/advertisements/useAdvertisements';
import useImageUpload from '../../features/fileUplad/useImageUpload';
import useVideoUpload from '../../features/fileUplad/useVideoUpload';
import { useServicePoints } from '../../features/servicePoint/useServicePoints';
import { useHandleInputChange } from '../../hook/useHandleInputChange';
import { useInvalidLangs } from '../../hook/useInvalidLangs';
import useFetchData from './hook/useFetchData';
import { useOnUpdatePostClicked } from './hook/useOnUpdatePostClicked';
import { file_types } from './initialStates/file_types';
import { initialFormState } from './initialStates/update';
import InputText from '../../components/common/Input/inputText';
import _ from 'lodash';

const Update = () => {
    const [activeTab, setActiveTab] = useState('az');
    const languages = ['az', 'en', 'ru'];
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const location = useLocation();
    const id = location.state || {};
    const { getAdvertisementById } = useAdvertisements();
    const { allServicePoints, fetchAllData } = useServicePoints();

    const {
        uploadError: uploadErrorLarge,
        imageUrl: imageUrlLarge,
        isLoading: isLoadingLarge,
        setImageUrl: setImageUrlLarge,
        handleFileChange: handleFileChangeLarge
    } = useImageUpload();

    const {
        uploadError: uploadErrorSmall,
        imageUrl: imageUrlSmall,
        isLoading: isLoadingSmall,
        setImageUrl: setImageUrlSmall,
        handleFileChange: handleFileChangeSmall
    } = useImageUpload();

    const {
        uploadError: uploadErrorVideo,
        videoUrl: videoUrlLarge,
        isLoading: isLoadingVideo,
        setVideoUrl: setVideoUrlLarge,
        handleVideoChange: handleVideoChangeLarge
    } = useVideoUpload();

    const {
        uploadError: uploadErrorVideoSmall,
        videoUrl: videoUrlSmall,
        setVideoUrl: setVideoUrlSmall,
        handleVideoChange: handleVideoChangeSmall
    } = useVideoUpload();

    const { formStateById, fetching, fetchError } = useFetchData(getAdvertisementById, id, initialFormState, lang);
    const { formState, handleInputChange } = useHandleInputChange(formStateById, fetching);
    const { response, formError, loading, onUpdatePostClicked } = useOnUpdatePostClicked(
        formState,
        imageUrlLarge,
        imageUrlSmall,
        videoUrlLarge,
        videoUrlSmall,
        id
    );
    const { invalidLangs } = useInvalidLangs(formError, loading);

    const handleServicePointChange = (selected) => {
        handleInputChange({ target: { name: 'servicepoint_ids', value: selected } });
    };

    const handleFileInputChange = (e, fileType, isLarge) => {
        if (!fileType) {
            toast.error("Fayl tipi seçilməyib. Zəhmət olmasa əvvəlcə fayl tipini seçin.");
            return;
        }

        const file = e.target.files[0];
        const fileExtension = file?.type.split('/')[0];

        if ((fileType === 'video' && fileExtension !== 'video') ||
            (fileType === 'image' && fileExtension !== 'image')) {
            toast.error("Fayl tipi seçiminizə uyğun deyil. Zəhmət olmasa uyğun fayl seçin.");
            return;
        }

        if (fileType === 'video') {
            if (isLarge) {
                setVideoUrlLarge(null);
                handleVideoChangeLarge(e);
            } else {
                setVideoUrlSmall(null);
                handleVideoChangeSmall(e);
            }
        } else {
            if (isLarge) {
                setImageUrlLarge(null);
                handleFileChangeLarge(e);
            } else {
                setImageUrlSmall(null);
                handleFileChangeSmall(e);
            }
        }
    };
    useEffect(() => {
        const debouncedFetch = _.debounce(fetchAllData, 300);
        debouncedFetch();
        return debouncedFetch.cancel;
    }, []);

    if (fetching) {
        return <Loading isLoading={fetching} />;
    }
    return (
        <HelmetProvider>
            <div className="page-wrapper">
                <div className="page-content">
                    <div className="row">
                        <div className="col-xl-9 mx-auto">
                            <h6 className="mb-0 text-uppercase table-name">{t("table.updateAdvertisement")}</h6>
                            <hr />
                            <div className="card">
                                <div className="card-body">
                                    <div className="mb-4">
                                        <ul className="nav nav-tabs">
                                            {languages.map(lang => (
                                                <li className="nav-item" key={lang}>
                                                    <button
                                                        className={`nav-link ${invalidLangs.includes(lang) ? 'invalid-bg' : ''}   ${activeTab === lang ? 'active' : ''}`}
                                                        onClick={() => setActiveTab(lang)}
                                                    >
                                                        {lang.toUpperCase()}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="p-4 border rounded">
                                        <form className="row g-3">
                                            <InputMultyLang
                                                label={`${t('properties.name')} (${activeTab})`}
                                                id={`name-${activeTab}`}
                                                name="name"
                                                value={formState.name?.[activeTab] || ''}
                                                error={formError?.[`name.${activeTab}`]}
                                                onChange={(e) => handleInputChange(e, activeTab)}
                                            />

                                            <InputText
                                                name="forward"
                                                label={t('properties.forward')}
                                                value={formState?.forward}
                                                onChange={handleInputChange}
                                                formError={formError}
                                            />

                                            <InputNumber
                                                name="play_time"
                                                label={t('properties.play_time')}
                                                formError={formError}
                                                value={formState?.play_time}
                                                onChange={handleInputChange}
                                            />

                                            <InputSelect
                                                name="file_type"
                                                label={t("properties.file_type")}
                                                options={file_types}
                                                value={formState.file_type}
                                                onChange={handleInputChange}
                                                formError={formError}
                                                lang={lang}
                                            />
                                            <div className="col-md-6">
                                                <label htmlFor="file_path_small" className="form-label">{t('properties.file_small')}</label>
                                                <input
                                                    name='file_path_small'
                                                    onChange={(e) => handleFileInputChange(e, formState.file_type, false)}
                                                    type="file"
                                                    className={`form-control`}
                                                    id="file_path_small"
                                                />
                                                <ShowImage
                                                    error={uploadErrorSmall}
                                                    imageUrl={(videoUrlSmall || imageUrlSmall || formState.file_path_small) ?? (videoUrlSmall || imageUrlSmall || formState.file_path_small)}
                                                    setImageUrl={formState.file_type === 'video' ? setVideoUrlLarge : setImageUrlLarge}
                                                    loading={isLoadingLarge || isLoadingVideo}
                                                    formState={formState}
                                                />

                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="file_path_large" className="form-label">{t('properties.file_large')}</label>
                                                <input
                                                    name='file_path_large'
                                                    onChange={(e) => handleFileInputChange(e, formState.file_type, true)}
                                                    type="file"
                                                    className={`form-control`}
                                                    id="file_path_large"
                                                />
                                                <ShowImage
                                                    error={uploadErrorLarge}
                                                    imageUrl={(videoUrlLarge || imageUrlLarge || formState.file_path_large) ?? (videoUrlLarge || imageUrlLarge || formState.file_path_large)}
                                                    setImageUrl={formState.file_type === 'video' ? setVideoUrlSmall : setImageUrlSmall}
                                                    loading={loading}
                                                    formState={formState}
                                                />
                                            </div>

                                            <MultipleSelect
                                                options={allServicePoints}
                                                onSelectionChange={handleServicePointChange}
                                                selectedValues={formStateById?.servicepoint_ids}
                                                name="servicepoint_ids"
                                                field="name"
                                                label={t('servicePoint.servicePoints')}
                                                lang={lang}
                                                formError={formError}
                                            />

                                            {formError?.[`servicepoint_ids`] && (
                                                <div className="invalid-feedback">{formError[`servicepoint_ids`].join(', ')}</div>
                                            )}

                                            <Button
                                                label={t('properties.save')}
                                                onClick={onUpdatePostClicked}
                                                isLoading={loading || isLoadingLarge || isLoadingSmall || isLoadingVideo}
                                                disabled={loading || isLoadingLarge || isLoadingSmall || isLoadingVideo}
                                            />
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastNotification response={response || uploadErrorLarge || uploadErrorSmall || uploadErrorVideo || fetchError} loading={loading} />
        </HelmetProvider>
    );
};

export default Update;